import { BrokersSocietiesWithUsers } from "@dune/types";
import { Avatar, AvatarFallback, AvatarImage } from "@dune/ui";
import { formatDate, getFullAddress } from "@dune/utils";
import { ColumnDef } from "@tanstack/react-table";

export const brokerColumns: ColumnDef<BrokersSocietiesWithUsers>[] = [
	{
		accessorKey: "id",
		header: "Ref",
		cell: ({ row }) => row.original.id,
		enableSorting: true,
		sortingFn: (rowA, rowB) => {
			const idA = rowA.original.id ?? 0;
			const idB = rowB.original.id ?? 0;
			return idA - idB;
		},
	},
	{
		accessorKey: "denomination",
		header: "Raison Social",
		cell: ({ row }) => {
			const denomination = row.original.denomination;
			return denomination ? (
				<div className="truncate max-w-[180px]" title={denomination}>
					{denomination}
				</div>
			) : (
				<span className="text-gray-400">—</span>
			);
		},
		enableSorting: true,
	},
	{
		accessorKey: "users",
		header: "Correspondant",
		cell: ({ row }) => {
			const brokerData =
				row.original.users.find((user) => user.isSignatory === true) ||
				row.original.users[0];

			return (
				<div className="flex flex-row items-center">
					<Avatar>
						<AvatarImage src="/placeholder-avatar.jpg" alt="User" />
						<AvatarFallback>
							{brokerData
								? `${brokerData.firstname.charAt(0)}${brokerData.lastname.charAt(0)}`
								: "ND"}
						</AvatarFallback>
					</Avatar>
					{brokerData
						? `${brokerData.firstname} ${brokerData.lastname}`
						: "Non défini"}
				</div>
			);
		},
		enableSorting: true,
		sortingFn: (rowA, rowB) => {
			const brokerDataA =
				rowA.original.users.find((user) => user.isSignatory === true) ||
				rowA.original.users[0];
			const brokerDataB =
				rowB.original.users.find((user) => user.isSignatory === true) ||
				rowB.original.users[0];
			const nameA = brokerDataA
				? `${brokerDataA.firstname} ${brokerDataA.lastname}`
				: "Non défini";
			const nameB = brokerDataB
				? `${brokerDataB.firstname} ${brokerDataB.lastname}`
				: "Non défini";
			return nameA.localeCompare(nameB);
		},
	},
	{
		accessorKey: "headOfficeAddress",
		header: "Adresse",
		cell: ({ row }) => {
			const fullAddress = getFullAddress(row.original.headOfficeAddress);

			return fullAddress ? (
				<div className="truncate max-w-[200px]" title={fullAddress}>
					{fullAddress}
				</div>
			) : (
				<span className="text-gray-400">—</span>
			);
		},
		enableSorting: true,
		sortingFn: (rowA, rowB) => {
			const fullAddressA = getFullAddress(rowA.original.headOfficeAddress);
			const fullAddressB = getFullAddress(rowB.original.headOfficeAddress);
			return fullAddressA.localeCompare(fullAddressB);
		},
	},
	{
		accessorKey: "createdAt",
		header: "Date partenariat",
		cell: ({ row }) => formatDate(row.original.createdAt),
		enableSorting: true,
	},
	{
		accessorKey: "brokerDetails.networkName",
		header: "Réseau",
		cell: ({ row }) => {
			const networkName = row.original.brokerDetails?.networkName;
			return networkName ? (
				<div className="truncate max-w-[120px]" title={networkName}>
					{networkName}
				</div>
			) : (
				<span className="text-gray-400">—</span>
			);
		},
		enableSorting: true,
		sortingFn: (rowA, rowB) => {
			const networkA = rowA.original.brokerDetails?.networkName ?? "—";
			const networkB = rowB.original.brokerDetails?.networkName ?? "—";
			return networkA.localeCompare(networkB);
		},
	},
	{
		accessorKey: "brokerDetails.companyName",
		header: "Groupe",
		cell: ({ row }) => {
			const companyName = row.original.brokerDetails?.sirenGroup;
			return companyName ? (
				<div className="truncate max-w-[120px]" title={companyName}>
					{companyName}
				</div>
			) : (
				<span className="text-gray-400">—</span>
			);
		},
		enableSorting: true,
		sortingFn: (rowA, rowB) => {
			const groupA = rowA.original.brokerDetails?.companyName ?? "—";
			const groupB = rowB.original.brokerDetails?.companyName ?? "—";
			return groupA.localeCompare(groupB);
		},
	},
	{
		accessorKey: "brokerDetails.entities",
		header: "Entité",
		cell: ({ row }) => {
			const entities = row.original.brokerDetails?.entities;
			const entitiesText =
				Array.isArray(entities) && entities.length > 0
					? entities.join(", ")
					: "—";

			return (
				<div className="truncate max-w-[150px]" title={entitiesText}>
					{entitiesText}
				</div>
			);
		},
		enableSorting: true,
		sortingFn: (rowA, rowB) => {
			const entitiesA = rowA.original.brokerDetails?.entities ?? [];
			const entitiesB = rowB.original.brokerDetails?.entities ?? [];
			return (entitiesA.join(", ") || "—").localeCompare(
				entitiesB.join(", ") || "—",
			);
		},
	},
	{
		accessorKey: "brokerDetails.status",
		header: "Statut",
		cell: ({ row }) => {
			const { status } = row.original.brokerDetails ?? {};

			if (!status) {
				return "-";
			}

			return (
				<>
					{(status === "TO_ANALYZE" ||
						status === "TO_SIGN" ||
						status === "TO_RENEW") && (
						<div className="inline-flex items-center px-2 py-1 rounded-full text-orange-badge-text bg-orange-badge-background border border-orange-badge-border">
							{status === "TO_ANALYZE" && "À analyser"}
							{status === "TO_SIGN" && "À signer"}
							{status === "TO_RENEW" && "À renouveler"}
						</div>
					)}
					{status === "NEW" && (
						<div className="inline-flex items-center px-2 py-1 rounded-full text-blue-badge-text bg-blue-badge-background border border-blue-badge-border">
							Nouveau
						</div>
					)}
					{status === "PROGRESS" && (
						<div className="inline-flex items-center px-2 py-1 rounded-full text-green-badge-text bg-green-badge-background border border-green-badge-border">
							En cours
						</div>
					)}
					{status === "CANCELLED" && (
						<div className="inline-flex items-center px-2 py-1 rounded-full text-red-badge-text bg-red-badge-background border border-red-badge-border">
							Résilié
						</div>
					)}
				</>
			);
		},
		enableSorting: true,
		sortingFn: (rowA, rowB) => {
			const statusA = rowA.original.brokerDetails?.status ?? "";
			const statusB = rowB.original.brokerDetails?.status ?? "";
			return statusA.localeCompare(statusB);
		},
	},
];
